/** Работа с камерой, микрофоном и превью. */

/** Кадр из видеопотока в двух размерах: оригинал и превью. */
export async function grabFrame(video: HTMLVideoElement): Promise<{ full: Blob; preview: Blob }> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('камера ещё не отдала кадр');

  const full = await draw(video, w, h, 'image/jpeg', 0.92);

  // Превью: длинная сторона 1600 px. Каталог, поиск и сравнение живут на нём;
  // оригинал нужен, только когда в камень всматриваются.
  const scale = Math.min(1, 1600 / Math.max(w, h));
  const preview = await draw(video, Math.round(w * scale), Math.round(h * scale), 'image/webp', 0.82);

  return { full, preview };
}

function draw(src: CanvasImageSource, w: number, h: number, mime: string, q: number): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('нет 2d-контекста');
  ctx.drawImage(src, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('не удалось закодировать кадр'))),
      mime,
      q,
    );
  });
}

/**
 * Кадр из готового файла — тем же весом, что и снятый в приложении.
 *
 * Нужен, когда камень уже заведён, а доснять его надо позже: системная
 * камера отдаёт снимок на 4–5 МБ, и класть его в базу как есть — верный
 * способ забить телефон за день выставки.
 */
export async function frameFromFile(file: Blob): Promise<{ full: Blob; preview: Blob }> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width: w, height: h } = bitmap;
    if (!w || !h) throw new Error('файл не похож на снимок');
    const big = Math.min(1, 3000 / Math.max(w, h));
    const full = await draw(bitmap, Math.round(w * big), Math.round(h * big), 'image/jpeg', 0.92);
    const scale = Math.min(1, 1600 / Math.max(w, h));
    const preview = await draw(bitmap, Math.round(w * scale), Math.round(h * scale), 'image/webp', 0.82);
    return { full, preview };
  } finally {
    bitmap.close();
  }
}

export async function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 3840 },
      height: { ideal: 2160 },
    },
    audio: false,
  });
}

/** Safari не умеет webm/opus — выбираем то, что реально поддерживается. */
export function audioMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

export class VoiceRecorder {
  private rec?: MediaRecorder;
  private chunks: Blob[] = [];
  private stream?: MediaStream;
  private startedAt = 0;

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const mime = audioMime();
    this.rec = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => { if (e.data.size) this.chunks.push(e.data); };
    this.rec.start(250);
    this.startedAt = Date.now();
  }

  get active() {
    return this.rec?.state === 'recording';
  }

  async stop(): Promise<{ blob: Blob; mime: string; durationMs: number } | null> {
    const rec = this.rec;
    if (!rec) return null;
    const done = new Promise<void>((r) => { rec.onstop = () => r(); });
    rec.stop();
    await done;
    this.stream?.getTracks().forEach((t) => t.stop());
    const mime = rec.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mime });
    this.rec = undefined;
    this.stream = undefined;
    return { blob, mime, durationMs: Date.now() - this.startedAt };
  }

  cancel() {
    try { this.rec?.stop(); } catch { /* уже остановлен */ }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.rec = undefined;
    this.stream = undefined;
  }
}

/** Не давать экрану гаснуть во время долгой заливки или съёмки. */
export async function keepAwake(): Promise<() => void> {
  try {
    const anyNav = navigator as any;
    if (!anyNav.wakeLock) return () => {};
    const lock = await anyNav.wakeLock.request('screen');
    return () => { try { lock.release(); } catch { /* уже отпущен */ } };
  } catch {
    return () => {};
  }
}
